import * as React from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';

import withStyles, {
  WithStyles,
  StyleRulesCallback
} from '@material-ui/core/styles/withStyles';

import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';

import {
  BaseMap,
  DocumentGrid,
  GeotiffOverlay,
  RasterMapReference,
  EditBinsDialog,
  NdviRange
} from '../components';

import {
  FieldProvider,
  LayoutToolsProvider,
  LayoutTools,
  FarmLibraryProvider
} from '../providers';

import AnalyzeZonesProvider, {
  ZoneAnalysisParams,
  ZoneAnalysisResult
} from '../providers/AnalyzeZonesProvider';

import UpsertDocumentProvider, {
  UpsertDocumentFn,
  SaveZoneAnalysisParams
} from '../providers/UpsertDocumentProvider';

import {
  Paper,
  Typography,
  Button,
  Snackbar,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Collapse,
  Checkbox,
  IconButton
} from '@material-ui/core';

import { DatePicker } from 'material-ui-pickers';

import BackIcon from '@material-ui/icons/ArrowBack';
import NdviIcon from '@material-ui/icons/Photo';
import TodayIcon from '@material-ui/icons/Today';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';

import { Stepper, Step, StepLabel, StepContent } from '@material-ui/core';
import { GeotiffRender } from '../utils/renderGeotiffRaster';
import SidePanel from './SidePanel';
import { calculateFieldViewport } from '../utils';

interface Renders {
  layout: LayoutTools;
  loading: boolean;
  field: Field | null;
  farm: Farm | null;
  layers: RasterDocument[] | null;
  layersFetchMore: () => void;
  execute: (params: ZoneAnalysisParams) => void;
  save: UpsertDocumentFn;
  processing: boolean;
  isBlankSlate: boolean;
  heatmapUrl: string | null;
}

interface State {
  activeStep: number;
  rasterDoc: RasterDocument | null;
  rasterRender: GeotiffRender | null;
  customBins: NdviRasterBins | null;
  showBinsDialog: boolean;
  result: ZoneAnalysisResult | null;
  criticalMonthsOpen: boolean;
  criticalMonths: number[];
  dateMenuOpen: boolean;
  capturedSince: number | null;
  capturedUntil: number | null;
  ndviRangeOpen: boolean;
  ndviRangeValue: [number, number] | undefined;
  outputRender: GeotiffRender | null;
  saving: boolean;
}

const styles: StyleRulesCallback = theme => ({
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    height: '100vh'
  },
  stepper: {
    marginTop: theme.spacing.unit * 2
  },
  content: {
    padding: theme.spacing.unit * 3,
    flex: 1,
    overflowY: 'scroll',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    justifyContent: 'center'
  },
  contentCentered: {
    padding: theme.spacing.unit * 3,
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  },
  map: {
    flex: 1
  }
});

type Props = WithStyles & RouteComponentProps<any>;

class ZoneAnalysis extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      activeStep: 0,
      rasterDoc: null,
      rasterRender: null,
      customBins: null,
      showBinsDialog: false,
      result: null,
      criticalMonthsOpen: false,
      criticalMonths: [],
      dateMenuOpen: false,
      capturedSince: null,
      capturedUntil: null,
      ndviRangeOpen: false,
      ndviRangeValue: undefined,
      outputRender: null,
      saving: false
    };
  }

  handleSelectClick = async (renders: Renders) => {
    const { rasterDoc } = this.state;

    if (!rasterDoc) {
      return;
    }

    await this.setState({ activeStep: 1 });
  };

  handleExecuteClick = async (renders: Renders) => {
    await this.setState({ activeStep: 2 });

    const callback = () => {
      this.setState({
        activeStep: 3,
        result: {
          id: 'fakeid',
          rasterUrl:
            'https://s3-sa-east-1.amazonaws.com/qumir/plots/627b1237cb2f4fe2b59475e9d2a3f3b0.tif'
        }
      });
    };
    setTimeout(callback, 5 * 1000);
  };

  handleExecuteRealClick = async (renders: Renders) => {
    const { rasterDoc, rasterRender } = this.state;

    if (!rasterDoc || !rasterRender || !rasterRender.bins) {
      return;
    }

    await this.setState({ activeStep: 2 });

    renders.execute({
      sourceRaster: rasterDoc.rasterUrl,
      bins: rasterRender.bins
    });
  };

  handleProcessCompleted = (result: ZoneAnalysisResult) => {
    this.setState({ activeStep: 3, result });
  };

  handleProcessError = (layers: RasterDocument[] | null, error: any) => {
    console.error(error);
    alert(error);
  };

  handleSave = (renders: Renders) => {
    const { result, rasterDoc, outputRender } = this.state;

    const field = renders.field as Field;

    if (!result || !rasterDoc || !outputRender) {
      return;
    }

    this.setState({ saving: true });

    renders.save({
      kind: 'zone-analysis',
      farmId: (field.farm as Farm).id,
      fieldId: field.id,
      rasterUrl: result.rasterUrl,
      thumbnailUrl: outputRender.dataUrl,
      labels: outputRender.labels,
      sourceId: rasterDoc.id
    } as SaveZoneAnalysisParams);
  };

  handleSaveCompleted = () => {
    this.setState({ saving: false });
  };

  renderLoading(message?: string | null) {
    return (
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={true}
        message={
          <>
            <CircularProgress color="secondary" size={25} />
            <span>{message || 'cargando'}</span>
          </>
        }
      />
    );
  }

  handleSourceRender = (output: GeotiffRender) => {
    this.setState({ rasterRender: output });
  };

  handleOutputRender = (output: GeotiffRender) => {
    this.setState({ outputRender: output });
  };

  renderBinsMap(renders: Renders) {
    const { classes } = this.props;
    const { rasterDoc, rasterRender, customBins, showBinsDialog } = this.state;
    const field = renders.field as Field;
    const viewport = calculateFieldViewport(field);

    if (!rasterDoc) {
      return;
    }

    return (
      <>
        <BaseMap className={classes.map} viewport={viewport}>
          <GeotiffOverlay
            id={field.id}
            geotiffType="grayscale"
            geotiffUrl={rasterDoc.rasterUrl}
            inputDomain={[1, 255]}
            outputDomain={[0, 1]}
            nodata={0}
            customBins={customBins || null}
            geometry={field.geometry}
            onRender={output => this.handleSourceRender(output)}
          />
          <RasterMapReference
            open={true}
            render={rasterRender}
            totalArea={field.area}
            onClick={() => this.setState({ showBinsDialog: true })}
          />
        </BaseMap>
        {rasterRender && (
          <EditBinsDialog
            bins={rasterRender.bins}
            histogram={rasterRender.histogram}
            totalArea={field.area}
            open={showBinsDialog}
            onClose={() => this.setState({ showBinsDialog: false })}
            onSubmit={newBins =>
              this.setState({ customBins: newBins, showBinsDialog: false })
            }
          />
        )}
      </>
    );
  }

  renderResultMap(renders: Renders) {
    const { classes } = this.props;
    const { result, outputRender } = this.state;
    const field = renders.field as Field;
    const viewport = calculateFieldViewport(field);

    if (!result) {
      return;
    }

    return (
      <BaseMap className={classes.map} viewport={viewport}>
        <GeotiffOverlay
          id={result.id}
          geotiffType="label"
          geotiffUrl={result.rasterUrl}
          inputDomain={[1, 10]}
          outputDomain={[1, 10]}
          customBins={null}
          nodata={0}
          geometry={field.geometry}
          onRender={output => this.handleOutputRender(output)}
        />
        <RasterMapReference
          open={true}
          render={outputRender}
          totalArea={field.area}
          onClick={() => this.setState({ showBinsDialog: true })}
        />
      </BaseMap>
    );
  }

  renderBlankSlate(renders: Renders) {
    const { classes } = this.props;
    return (
      <Paper square={true} elevation={1} className={classes.contentCentered}>
        <Typography variant="title" color="textSecondary">
          No hay capas disponibles para realizar el análisis.
        </Typography>
      </Paper>
    );
  }

  renderSupplyGrid(renders: Renders) {
    const { classes } = this.props;
    const { rasterDoc } = this.state;

    if (renders.isBlankSlate) {
      return this.renderBlankSlate(renders);
    }

    return (
      <Paper square={true} elevation={1} className={classes.content}>
        <DocumentGrid
          selectable={true}
          documents={renders.layers}
          loading={false}
          onFetchMore={renders.layersFetchMore}
          selectedIDs={rasterDoc ? [rasterDoc.id] : []}
          onSelect={(doc, checked) => {
            this.setState({ rasterDoc: doc as RasterDocument });
          }}
        />
      </Paper>
    );
  }

  renderBigLoading() {
    const { classes } = this.props;
    return (
      <Paper square={true} elevation={1} className={classes.contentCentered}>
        <CircularProgress size={150} />
        <Typography variant="title" color="textSecondary">
          Ejecutando Agro-ritmo
        </Typography>
      </Paper>
    );
  }

  handleToggleMonth(month: number) {
    if (this.state.criticalMonths.find(m => m === month)) {
      this.setState({
        criticalMonths: this.state.criticalMonths.filter(m => m !== month)
      });
    } else {
      this.setState({
        criticalMonths: [...this.state.criticalMonths, month]
      });
    }
  }

  renderMonthCheckbox(monthNumber: number, monthName: string) {
    return (
      <ListItem key={monthNumber} dense={true}>
        <ListItemText primary={monthName} />
        <ListItemSecondaryAction>
          <Checkbox
            checked={this.state.criticalMonths.indexOf(monthNumber) > -1}
            onChange={() => this.handleToggleMonth(monthNumber)}
          />
        </ListItemSecondaryAction>
      </ListItem>
    );
  }

  renderFilters() {
    return (
      <List>
        <ListItem
          button={true}
          onClick={() =>
            this.setState({
              dateMenuOpen: !this.state.dateMenuOpen
            })
          }
        >
          <ListItemIcon>
            <TodayIcon />
          </ListItemIcon>
          <ListItemText primary="Filtrar por Fecha" />
          {this.state.dateMenuOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItem>
        <Collapse in={this.state.dateMenuOpen}>
          <List style={{ paddingBottom: 16 }}>
            <ListItem style={{ paddingTop: 0, paddingBottom: 0 }}>
              <DatePicker
                fullWidth={true}
                label="Desde"
                clearable={true}
                disableFuture={true}
                value={this.state.capturedSince}
                onChange={d => this.setState({ capturedSince: d })}
                maxDate={this.state.capturedUntil || undefined}
                clearLabel="Limpiar"
                labelFunc={date => (date ? date.format('DD/MM/YYYY') : '')}
                margin="dense"
              />
            </ListItem>
            <ListItem style={{ paddingTop: 0, paddingBottom: 0 }}>
              <DatePicker
                fullWidth={true}
                label="Hasta"
                clearable={true}
                disableFuture={true}
                value={this.state.capturedUntil}
                onChange={d => this.setState({ capturedUntil: d })}
                minDate={this.state.capturedSince || undefined}
                clearLabel="Limpiar"
                labelFunc={date => (date ? date.format('DD/MM/YYYY') : '')}
                margin="dense"
              />
            </ListItem>
          </List>
        </Collapse>

        <Divider />

        <ListItem
          button={true}
          onClick={() =>
            this.setState({
              criticalMonthsOpen: !this.state.criticalMonthsOpen
            })
          }
        >
          <ListItemIcon>
            <TodayIcon />
          </ListItemIcon>
          <ListItemText primary="Meses críticos" />
          {this.state.criticalMonthsOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItem>
        <Collapse in={this.state.criticalMonthsOpen}>
          <List style={{ maxHeight: 250, overflowY: 'scroll' }}>
            {this.renderMonthCheckbox(1, 'Enero')}
            {this.renderMonthCheckbox(2, 'Febrero')}
            {this.renderMonthCheckbox(3, 'Marzo')}
            {this.renderMonthCheckbox(4, 'Abril')}
            {this.renderMonthCheckbox(5, 'Mayo')}
            {this.renderMonthCheckbox(6, 'Junio')}
            {this.renderMonthCheckbox(7, 'Julio')}
            {this.renderMonthCheckbox(8, 'Agosto')}
            {this.renderMonthCheckbox(9, 'Septiembre')}
            {this.renderMonthCheckbox(10, 'Octubre')}
            {this.renderMonthCheckbox(11, 'Noviembre')}
            {this.renderMonthCheckbox(12, 'Diciembre')}
          </List>
        </Collapse>

        <Divider />

        <ListItem
          button={true}
          onClick={() =>
            this.setState({
              ndviRangeOpen: !this.state.ndviRangeOpen
            })
          }
        >
          <ListItemIcon>
            <NdviIcon />
          </ListItemIcon>
          <ListItemText primary="Rango de NDVI" />
          {this.state.ndviRangeOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItem>
        <Collapse in={this.state.ndviRangeOpen}>
          <List style={{ paddingBottom: 16, paddingTop: 16 }}>
            <ListItem
              style={{
                paddingTop: 0,
                paddingBottom: 0,
                flexDirection: 'column'
              }}
            >
              <NdviRange
                value={this.state.ndviRangeValue}
                onChange={data =>
                  this.setState({
                    ndviRangeValue: data
                  })
                }
              />
            </ListItem>
          </List>
        </Collapse>
      </List>
    );
  }

  composeProviders(children: (r: Renders) => React.ReactNode) {
    const {
      match: {
        params: { fieldId }
      }
    } = this.props;

    return (
      <LayoutToolsProvider>
        {layout => (
          <FieldProvider
            fieldId={fieldId}
            includeFarm={true}
            includeLayers={false}
          >
            {fieldRenders => (
              <FarmLibraryProvider
                farmId={
                  (fieldRenders.farm && fieldRenders.farm.id) || undefined
                }
                first={9}
                filter={{
                  kind: ['raster-ndvi', 'yield-analysis'],
                  fieldId: fieldId,
                  criticalMonths: this.state.criticalMonths,
                  capturedSince: this.state.capturedSince,
                  capturedUntil: this.state.capturedUntil,
                  minMeanNdvi:
                    this.state.ndviRangeValue && this.state.ndviRangeValue[0],
                  maxMeanNdvi:
                    this.state.ndviRangeValue && this.state.ndviRangeValue[1]
                }}
              >
                {libResult => (
                  <AnalyzeZonesProvider
                    onCompleted={this.handleProcessCompleted}
                    onError={error =>
                      this.handleProcessError(fieldRenders.layers, error)
                    }
                  >
                    {analyzeRenders => (
                      <UpsertDocumentProvider
                        onCompleted={this.handleSaveCompleted}
                      >
                        {(save: UpsertDocumentFn) =>
                          children({
                            layout,
                            loading: fieldRenders.loading || libResult.loading,
                            field: fieldRenders.field,
                            farm: fieldRenders.farm,
                            layers: libResult.documents as RasterDocument[],
                            execute: analyzeRenders.execute,
                            save: save,
                            heatmapUrl: null,
                            processing: analyzeRenders.processing,
                            layersFetchMore: libResult.fetchMore,
                            isBlankSlate:
                              !fieldRenders.loading &&
                              !libResult.loading &&
                              libResult.documents != null &&
                              !libResult.documents.length
                          })
                        }
                      </UpsertDocumentProvider>
                    )}
                  </AnalyzeZonesProvider>
                )}
              </FarmLibraryProvider>
            )}
          </FieldProvider>
        )}
      </LayoutToolsProvider>
    );
  }

  renderStepper(renders: Renders) {
    const { classes } = this.props;
    const { rasterDoc } = this.state;
    return (
      <Stepper
        activeStep={this.state.activeStep}
        orientation="vertical"
        className={classes.stepper}
      >
        <Step key={0}>
          <StepLabel>Seleccionar Insumos</StepLabel>
          <StepContent>
            <Typography variant="subheading" gutterBottom={true}>
              Elija de su biblioteca el raster que desea utilizar como base para
              la definición de las zonas de gestión de su lote.
            </Typography>
            <Button
              fullWidth={true}
              color="primary"
              variant="raised"
              disabled={!rasterDoc}
              onClick={() => this.handleSelectClick(renders)}
            >
              Seleccionar
            </Button>
            {!rasterDoc && (
              <Typography color="error">
                Debe seleccionar un insumo para su análisis
              </Typography>
            )}
          </StepContent>
        </Step>
        <Step key={1}>
          <StepLabel>Definir Cortes</StepLabel>
          <StepContent>
            <Typography variant="subheading">
              Ajuste los intervalos de valores del raster para definir la
              cantidad y extensión de sus zonas de gestión.
            </Typography>
            <Button
              fullWidth={true}
              color="primary"
              variant="raised"
              disabled={!rasterDoc}
              onClick={() => this.handleExecuteRealClick(renders)}
            >
              Procesar
            </Button>
          </StepContent>
        </Step>
        <Step key={2}>
          <StepLabel>Procesar Insumos</StepLabel>
          <StepContent>
            <Typography variant="subheading">
              El servidor esta normalizando los valores de cada insumo,
              calculando el promedio de cada punto y definiendo ambientes
              homogeneos dentro del lote.
            </Typography>
          </StepContent>
        </Step>
        <Step key={3}>
          <StepLabel>Validar Resultado</StepLabel>
          <StepContent>
            <Typography variant="subheading" gutterBottom={true}>
              Este es el resultado del analisis. Si esta conforme con los
              ambientes generados, puede guardar el analisis en su biblioteca.
            </Typography>
            <Button
              fullWidth={true}
              color="primary"
              variant="raised"
              style={{ marginBottom: 20 }}
              onClick={() => this.handleSave(renders)}
              disabled={this.state.saving}
            >
              Guardar en biblioteca
            </Button>
            <Button
              fullWidth={true}
              variant="raised"
              onClick={() => this.setState({ activeStep: 0 })}
            >
              Volver a comenzar
            </Button>
          </StepContent>
        </Step>
      </Stepper>
    );
  }

  render() {
    const { classes } = this.props;

    const { activeStep, saving } = this.state;

    return this.composeProviders(renders => (
      <main className={classes.root}>
        {renders.loading && this.renderLoading('cargando lote')}
        {saving && this.renderLoading('guardando análisis')}

        <SidePanel>
          <div style={{ display: 'flex' }}>
            <div style={{ flex: 0, marginRight: 16, alignItems: 'center' }}>
              <IconButton onClick={() => this.props.history.goBack()}>
                <BackIcon />
              </IconButton>
            </div>
            <div style={{ flex: 1 }}>
              <Typography variant="subheading">Zonas de Gestión</Typography>
              <Typography variant="headline" style={{ marginBottom: 16 }}>
                {(renders.field && renders.field.name) || 'Cargando Lote...'}
              </Typography>
            </div>
          </div>
          {activeStep === 0 && this.renderFilters()}
          {this.renderStepper(renders)}
        </SidePanel>

        {activeStep === 0 && this.renderSupplyGrid(renders)}
        {activeStep === 1 && this.renderBinsMap(renders)}
        {activeStep === 2 && this.renderBigLoading()}
        {activeStep === 3 && this.renderResultMap(renders)}
      </main>
    ));
  }
}

export default withStyles(styles)(withRouter(ZoneAnalysis));



// WEBPACK FOOTER //
// ./src/containers/ZoneAnalysis.tsx